import { formatValue, type Board as BoardData, type Entry } from "@/lib/leaderboards";

const same = (a: Entry, b: Entry) => a.submittedAt === b.submittedAt && a.initials === b.initials;

// Three states, in the family's convention: undefined is still loading, null
// could not be reached, and an empty list is a real, empty board.
export function Board({
  board,
  mark,
  className,
}: {
  board: BoardData | null | undefined;
  mark?: Entry | null;
  className?: string;
}) {
  const shell = `rounded-lg bg-pine-deep/60 px-3 py-2 text-left ${className ?? ""}`;
  const eyebrow = "text-xs font-medium uppercase tracking-[0.18em] text-ice";
  if (board === undefined) {
    return (
      <div className={shell}>
        <p className={eyebrow}>Arcade board</p>
        <p className="mt-1 text-sm text-snow-dim">Reading the board…</p>
      </div>
    );
  }
  if (board === null) {
    return (
      <div className={shell}>
        <p className={eyebrow}>Arcade board</p>
        <p className="mt-1 text-sm text-snow-dim">The board is out of reach right now.</p>
      </div>
    );
  }
  return (
    <div className={shell}>
      <p className={eyebrow}>Arcade board</p>
      {board.entries.length === 0 ? (
        <p className="mt-1 text-sm text-snow-dim">No runs on the board yet. Be first.</p>
      ) : (
        <table className="board-table mt-1 w-full border-collapse text-sm tabular-nums">
          <thead>
            <tr className="text-xs uppercase tracking-[0.12em] text-ice">
              <th scope="col" className="py-1 pr-2 text-left font-medium">
                #
              </th>
              <th scope="col" className="py-1 pr-2 text-left font-medium">
                Initials
              </th>
              {board.game.columns.map((column) => (
                <th key={column.field} scope="col" className="py-1 pl-2 text-right font-medium">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {board.entries.map((entry) => {
              const mine = mark ? same(entry, mark) : false;
              return (
                <tr
                  key={`${entry.rank}-${entry.submittedAt}`}
                  aria-current={mine ? "true" : undefined}
                  className={
                    mine
                      ? "mine border-t border-snow/10 bg-ice/15 text-snow shadow-[inset_3px_0_0_var(--color-ice-bright)]"
                      : "border-t border-snow/10 text-snow"
                  }
                >
                  <td className="py-1 pr-2 text-ice-bright">{entry.rank}</td>
                  <td className="initials py-1 pr-2 font-mono tracking-[0.2em]">
                    {entry.initials}
                  </td>
                  {board.game.columns.map((column) => (
                    <td key={column.field} className="py-1 pl-2 text-right font-mono">
                      {formatValue(entry[column.field], column.format)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
